import type { Day, Instructor, Slot } from '@/state/class-availabilities';
import type { CalendarAvailability, CalendarSlot } from '@/components/calendar';
import { getAbbreviatedName } from '@/lib/utils';

/**
 * Transforms API availability data into the CalendarAvailability format,
 * applying instructor and slot filters. No selected ids means every
 * instructor.
 */
export function transformClassAvailability(
  days: Day[] | undefined,
  selectedInstructorIds: string[],
  excludeSlotId?: string | null,
  excludeStartTime?: string | null
): CalendarAvailability {
  if (!days?.length) return {};

  const result: CalendarAvailability = {};

  for (const day of days) {
    const dateKey =
      typeof day.date === 'string'
        ? day.date.slice(0, 10)
        : `${day.date.getFullYear()}-${String(day.date.getMonth() + 1).padStart(2, '0')}-${String(day.date.getDate()).padStart(2, '0')}`;

    const slots: CalendarSlot[] = [];

    for (const inst of day.instructors) {
      if (
        selectedInstructorIds.length > 0 &&
        !selectedInstructorIds.includes(inst.admin_user_id)
      )
        continue;

      const sortedSlots = [...inst.slots].sort((a, b) => a.start_time.localeCompare(b.start_time));
      for (const slot of sortedSlots) {
        const slotId = slot.slot_id ?? slot.override_slot_id ?? '';
        if (excludeSlotId && slotId === excludeSlotId && excludeStartTime && slot.start_time === excludeStartTime) continue;

        slots.push({
          id: slotId,
          overrideSlotId: slot.override_slot_id,
          startTime: slot.start_time,
          endTime: slot.end_time,
          timezone: slot.timezone,
          availableSpots: slot.available_spots ?? undefined,
          totalSpots: slot.total_spots ?? undefined,
          isFullyBooked: slot.is_fully_booked,
          instructorName: `${inst.first_name} ${inst.last_name}`.trim(),
          instructorId: inst.admin_user_id,
          instructorAvatar: inst.profile_picture ?? undefined,
          instructorScheduleId: inst.instructor_schedule_id,
          isSubstitute: slot.is_substituted,
          waitlistEnabled: slot.waitlist_enabled,
          waitlistCount: slot.waitlist_count,
        });
      }
    }

    if (slots.length > 0) {
      result[dateKey] = slots;
    }
  }

  return result;
}

/**
 * Extracts unique instructors from all days, deduplicated by admin_user_id.
 */
export function getUniqueInstructors(days: Day[] | undefined): Instructor[] {
  if (!days?.length) return [];

  const seen = new Set<string>();
  const instructors: Instructor[] = [];

  for (const day of days) {
    for (const inst of day.instructors) {
      if (!seen.has(inst.admin_user_id)) {
        seen.add(inst.admin_user_id);
        instructors.push(inst);
      }
    }
  }

  return instructors.sort((a, b) =>
    getAbbreviatedName(a.first_name, a.last_name).localeCompare(
      getAbbreviatedName(b.first_name, b.last_name)
    )
  );
}

// "All" is stored as no ids, not as the ids on screen: the instructors come
// from the month loaded, and a list would hide whoever first teaches in a
// later month, a substitute say. Naming everyone on screen, or no one on it,
// counts as All.
export function normalizeInstructorSelection(
  ids: string[],
  instructors: Instructor[]
): string[] {
  const shown = instructors.filter((i) => ids.includes(i.admin_user_id)).length;
  return shown === 0 || shown === instructors.length ? [] : ids;
}

// A saved filter outlives the class's instructors: after a class moves to
// another instructor its old ids hide every time the class now has, and a
// class with one instructor renders no filter to clear them with.
export function restoreSelectedInstructorIds(
  savedIds: string[],
  instructors: Instructor[]
): string[] {
  const currentIds = instructors.map((i) => i.admin_user_id);
  return normalizeInstructorSelection(
    savedIds.filter((id) => currentIds.includes(id)),
    instructors
  );
}
